/**
 * Значок типа задачи на карточке (§7.4).
 *
 * Значок берётся из справочника — поля iconKey, а не из названия типа.
 * Сопоставление по названию («если тип называется „Ошибка“») ломается
 * при первом же переименовании: справочник типов правит администратор,
 * и «Ошибка» вполне может стать «Дефектом». Ключ переживает переименование,
 * потому что принадлежит строке справочника, а не её подписи.
 *
 * Перечень ключей закрытый. Ключ — это значение из базы, и подставлять
 * по нему произвольный компонент нельзя; незнакомый ключ просто не даёт
 * значка, а не роняет доску.
 */

import { Bug, Wrench } from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { cn } from "./cn";

/**
 * Обычная задача («circle» в справочнике) значка намеренно не получает.
 * Значок отмечает особый случай — ошибку или доработку; проставленный
 * на каждой карточке, он перестаёт что-либо отмечать и остаётся шумом.
 */
const ICONS: Record<string, { icon: LucideIcon; tone: string }> = {
  // Цвет — из токенов темы: в тёмной и контрастной темах он свой,
  // а смысл по-прежнему несут форма и подпись (§19.4).
  bug: { icon: Bug, tone: "text-danger" },
  wrench: { icon: Wrench, tone: "text-info" },
};

interface TaskTypeIconProps {
  // undefined в типе явно: поля карточки необязательны, а при
  // exactOptionalPropertyTypes «необязательное» и «может быть undefined» —
  // разные вещи.
  iconKey?: string | null | undefined;
  /** Название типа из справочника: оно и есть подпись значка. */
  name?: string | null | undefined;
  className?: string | undefined;
}

export function TaskTypeIcon({ iconKey, name, className }: TaskTypeIconProps) {
  const meta = typeof iconKey === "string" ? ICONS[iconKey] : undefined;
  if (meta === undefined) return null;
  const { icon: Icon, tone } = meta;

  const label = name ?? "Тип задачи";
  return (
    <span
      // §19.4: значок — не единственный носитель смысла. Форма отличает
      // ошибку от доработки, а доступное имя называет тип словами: без
      // него карточка для скринридера ничем не отличается от обычной.
      role="img"
      aria-label={`Тип: ${label}`}
      title={label}
      className={cn("inline-flex shrink-0", tone, className)}
    >
      <Icon className="size-3.5" aria-hidden />
    </span>
  );
}
